/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable max-lines */
import { Grid } from '@elliemae/ds-grid';
import { MenuCollapse, MenuExpand } from '@elliemae/ds-icons';
import { DSSkeleton } from '@elliemae/ds-skeleton';
import { SimpleTruncatedTooltipText } from '@elliemae/ds-truncated-tooltip-text';
import React, { useCallback, useContext, useMemo } from 'react';
import LeftNavigationContext from '../../LeftNavigationContext.js';
import { FOOTER_DS_ID, FOOTER_MENU_CLOSE_DS_ID, LEFT_NAVIGATION_DATA_TESTID } from '../../constants/index.js';
import { useKeyboardNavigation } from '../../hooks/index.js';
import {
  StyledFooterItem,
  StyledFooterLabel,
  StyledFooterMenu,
  StyledFooterSeparator,
  StyledMenuCollapse,
} from './styled.js';

type LeftNavFooterItemProps = {
  footerLabel: string;
  FooterLabelComponent?: React.ComponentType<unknown>;
  onFooterExpand: React.MouseEventHandler & React.KeyboardEventHandler;
  onFooterClose: React.MouseEventHandler & React.KeyboardEventHandler;
};

// eslint-disable-next-line complexity
export const LeftNavFooterItem: React.ComponentType<LeftNavFooterItemProps> = (props) => {
  const { footerLabel, FooterLabelComponent, onFooterExpand = () => null, onFooterClose = () => null } = props;
  const {
    leftNavProps: { labelOverflow, isSkeleton, hideFooterPipe, expanded },
    expandedForAnimation,
    setFocusedItem,
    focusedItem,
    visibleItemsRefs,
    leftNavProps,
  } = useContext(LeftNavigationContext);
  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);
  const getOwnerPropsArguments = React.useCallback(() => ({}), []);
  const id = FOOTER_DS_ID;

  const handleOnClick = expandedForAnimation ? onFooterClose : onFooterExpand;

  const onKeyDown = useKeyboardNavigation({
    item: {
      dsId: id,
      itemOpts: {
        selectable: !expandedForAnimation,
        openable: false,
        closable: false,
      },
    },
    onClick: handleOnClick,
  });

  const handleMenuCollapseClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleOnClick(e as React.MouseEvent<HTMLButtonElement, MouseEvent>);
      setFocusedItem(id);
    },
    [handleOnClick, id, setFocusedItem],
  );

  const label = useMemo(() => {
    if (FooterLabelComponent) return <FooterLabelComponent />;
    if (labelOverflow === 'truncate')
      return <SimpleTruncatedTooltipText value={footerLabel} placement="bottom-start" />;
    return footerLabel;
  }, [footerLabel, FooterLabelComponent, labelOverflow]);

  const postAnimationButtonFocusHandler = useCallback(
    (newHTMLNode: HTMLButtonElement | null) => {
      if (newHTMLNode === null || !expanded) {
        return;
      }
      if (focusedItem === id) {
        newHTMLNode.focus();
      }
    },
    [focusedItem, id, expanded],
  );

  if (isSkeleton) {
    return (
      <StyledFooterMenu
        key={id}
        data-testid={LEFT_NAVIGATION_DATA_TESTID.FOOTER_MENU} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        <Grid height="48px" justifyContent="center" alignItems="center" cols={['1fr']} p="xxs">
          <DSSkeleton variant="rectangular" height="24px" />
        </Grid>
      </StyledFooterMenu>
    );
  }

  return (
    <StyledFooterMenu
      key={id}
      data-testid={LEFT_NAVIGATION_DATA_TESTID.FOOTER_MENU} // https://jira.elliemae.io/browse/PUI-15589
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
    >
      <StyledFooterItem
        innerRef={visibleItemsRefs.current[id]}
        tabIndex={expandedForAnimation ? -1 : 0}
        expanded={expandedForAnimation}
        pt="xxs2"
        height={expandedForAnimation ? 'auto' : '48px'}
        alignItems="flex-start"
        pl="xxs2"
        cols={expandedForAnimation ? ['auto', '1fr', 'auto'] : ['38px']}
        onClick={expandedForAnimation ? () => null : handleOnClick}
        onKeyDown={onKeyDown}
        onFocus={() => setFocusedItem(id)}
        role="menuitem"
        aria-label={
          expandedForAnimation
            ? `Footer, ${typeof footerLabel === 'string' ? footerLabel : ''}`
            : `Expand left navigation`
        }
        data-testid={LEFT_NAVIGATION_DATA_TESTID.FOOTER_ITEM} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {!expandedForAnimation ? (
          <MenuExpand
            size="m"
            color={['brand-primary', '800']}
            // https://jira.elliemae.io/browse/PUI-15589
            //   VERY SPECIAL CASE, THE SLOT FOOTER_BTN GOES TO THE ICON WHEN COLLAPSED, TO THE BTN WHEN EXPANDED
            data-testid={LEFT_NAVIGATION_DATA_TESTID.FOOTER_BTN}
          />
        ) : (
          <>
            <StyledFooterLabel
              mt="5px"
              mr="12px"
              mb="16px"
              labelOverflow={labelOverflow}
              aria-live="polite"
              data-testid={LEFT_NAVIGATION_DATA_TESTID.ITEM_LABEL} // https://jira.elliemae.io/browse/PUI-15589
              getOwnerProps={getOwnerProps}
              getOwnerPropsArguments={getOwnerPropsArguments}
            >
              {label}
            </StyledFooterLabel>
            {!hideFooterPipe ? (
              <StyledFooterSeparator
                mt="xxxs"
                data-testid={LEFT_NAVIGATION_DATA_TESTID.FOOTER_SEPARATOR} // https://jira.elliemae.io/browse/PUI-15589
                getOwnerProps={getOwnerProps}
                getOwnerPropsArguments={getOwnerPropsArguments}
              />
            ) : (
              <div></div>
            )}
            <StyledMenuCollapse
              innerRef={postAnimationButtonFocusHandler}
              buttonType="raw"
              onClick={handleMenuCollapseClick}
              tabIndex={0}
              onFocus={(e: React.FocusEvent) => {
                e.stopPropagation();
                setFocusedItem(FOOTER_MENU_CLOSE_DS_ID);
              }}
              role="button"
              aria-label="Collapse left navigation"
              //   VERY SPECIAL CASE, THE SLOT FOOTER_BTN GOES TO THE ICON WHEN COLLAPSED, TO THE BTN WHEN EXPANDED
              data-testid={LEFT_NAVIGATION_DATA_TESTID.FOOTER_BTN}
              getOwnerProps={getOwnerProps}
              getOwnerPropsArguments={getOwnerPropsArguments}
            >
              <MenuCollapse size="m" color={['brand-primary', '800']} />
            </StyledMenuCollapse>
          </>
        )}
      </StyledFooterItem>
    </StyledFooterMenu>
  );
};

export default LeftNavFooterItem;
